import { Fragment, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, CreditCard, Lock, MessageSquare, type LucideIcon } from "lucide-react";
import { useChat } from "./chat/ChatProvider";
import { useAgentArea } from "@/hooks/useAgentArea";
import { useAuth } from "@/auth/AuthContext";
import { useModules } from "@/auth/ModulesContext";
import { useOrganisationClientType } from "./PaymentsSettings";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Kbd } from "./ui/kbd";
import { cn } from "@/lib/utils";
import { moduleForbiddenMessage, type ModuleKey } from "@/lib/modules";
import { AGENT_AREAS, ORG_AREA, type NavPage } from "@/config/nav";
import { usePeriodLink } from "@/books/period";

function isSubItemActive(itemTo: string, pathname: string): boolean {
  return pathname === itemTo || pathname.startsWith(`${itemTo}/`);
}

function findActiveSubItemTo(pages: NavPage[], pathname: string): string | null {
  let best: string | null = null;
  for (const item of pages) {
    if (isSubItemActive(item.to, pathname) && (best === null || item.to.length > best.length)) {
      best = item.to;
    }
  }
  return best;
}

export function Sidebar() {
  return (
    <aside className="hidden w-[240px] shrink-0 flex-col border-r bg-sidebar/95 backdrop-blur-md md:flex">
      <SidebarContent />
    </aside>
  );
}

export function SidebarContent({ onNavigate }: { onNavigate?: () => void } = {}) {
  const { area } = useAgentArea();
  const { pathname } = useLocation();
  const { isOpen, isStreaming, error, toggleChat } = useChat();
  const { activeOrganisationId } = useAuth();
  const { hasModule, holdsModule } = useModules();
  const { clientType } = useOrganisationClientType(activeOrganisationId ?? null);
  // Manual expand/collapse is scoped to the current area; moving area drops it so the new page's section opens.
  const [manualState, setManualState] = useState<{
    area: string | null;
    toggled: Record<string, boolean>;
  }>({ area, toggled: {} });
  const manuallyToggled = manualState.area === area ? manualState.toggled : {};

  const agentStatus: "online" | "thinking" | "offline" = error
    ? "offline"
    : isStreaming
      ? "thinking"
      : "online";

  function isExpanded(id: string) {
    return manuallyToggled[id] ?? area === id;
  }

  function toggleSection(id: string, event: React.MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    setManualState({
      area,
      toggled: { ...manuallyToggled, [id]: !isExpanded(id) },
    });
  }

  function clearManualOverride(id: string) {
    if (!(id in manuallyToggled)) return;
    const next = { ...manuallyToggled };
    delete next[id];
    setManualState({ area, toggled: next });
  }

  const isLive = clientType === "Live";
  const envLabel = clientType ?? "Sandbox";
  const envBlurb = isLive
    ? "Real payment environment."
    : clientType === "Demo"
      ? "Demo environment · made-up data."
      : "Demo environment · test payments only.";

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pt-3">
        <nav className="flex flex-col gap-0.5 px-3">
          {AGENT_AREAS.map((item) => {
            if (item.kind === "soon") {
              const Icon = item.icon;
              return (
                <div
                  key={item.label}
                  className="flex cursor-not-allowed items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground opacity-55"
                  aria-disabled
                >
                  <span className="flex items-center gap-2.5">
                    <Icon className="size-4 text-muted-foreground" />
                    {item.label}
                  </span>
                  <span className="text-[9px] font-semibold uppercase tracking-wider">
                    Soon
                  </span>
                </div>
              );
            }

            const Icon = item.icon;

            if (item.hiddenWithoutModule && !holdsModule(item.module)) return null;
            // Unheld modules gray out rather than hide: showing what Dolfin offers is the point of the sandbox.
            if (!hasModule(item.module)) {
              return <ForbiddenNavItem key={item.id} label={item.label} icon={Icon} module={item.module} />;
            }

            return (
              <AreaNavSection
                key={item.id}
                label={item.label}
                icon={Icon}
                to={item.to}
                pages={item.pages}
                active={area === item.id}
                expanded={isExpanded(item.id)}
                pathname={pathname}
                onToggle={(e) => toggleSection(item.id, e)}
                onOpen={() => {
                  clearManualOverride(item.id);
                  onNavigate?.();
                }}
                onNavigate={onNavigate}
              />
            );
          })}
        </nav>
      </div>

      <div className="shrink-0 flex flex-col border-t bg-background">
        <nav className="flex flex-col gap-0.5 px-3 py-3">
          {/* Apart from the areas: the organisation serves every area rather than being one. */}
          <AreaNavSection
            label={ORG_AREA.label}
            icon={ORG_AREA.icon}
            to={ORG_AREA.to}
            pages={ORG_AREA.pages}
            active={area === ORG_AREA.id}
            expanded={isExpanded(ORG_AREA.id)}
            pathname={pathname}
            onToggle={(e) => toggleSection(ORG_AREA.id, e)}
            onOpen={() => {
              clearManualOverride(ORG_AREA.id);
              onNavigate?.();
            }}
            onNavigate={onNavigate}
          />
        </nav>

        <div className="flex flex-col gap-3 border-t p-3">
          <button
            type="button"
            onClick={() => {
              toggleChat();
              onNavigate?.();
            }}
            aria-pressed={isOpen}
            className={cn(
              "flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-xs transition hover:bg-muted",
              isOpen ? "border-accent-blue/40 bg-accent-blue/5" : "border-border",
              agentStatus === "online" && "ring-2 ring-emerald-500/40 ring-offset-2 ring-offset-background",
              agentStatus === "thinking" && "ring-2 ring-amber-400/50 ring-offset-2 ring-offset-background",
            )}
          >
            <AgentStatusDot status={agentStatus} />
            <span className="flex flex-1 flex-col leading-tight">
              <span className="text-[11px] font-semibold text-foreground">
                {agentStatus === "thinking"
                  ? "Agent thinking…"
                  : agentStatus === "offline"
                    ? "Agent offline"
                    : "Agent online"}
              </span>
              <span className="text-[10px] text-muted-foreground">
                {isOpen ? "Hide chat" : "Open chat"}
              </span>
            </span>
            <MessageSquare className="size-3.5 text-accent-blue" />
            <Kbd>&#8984;I</Kbd>
          </button>
        </div>
      </div>
    </div>
  );
}

function AreaNavSection({
  label,
  icon: Icon,
  to,
  pages,
  active,
  expanded,
  pathname,
  onToggle,
  onOpen,
  onNavigate,
}: {
  label: string;
  icon: LucideIcon;
  to: string;
  pages: NavPage[];
  active: boolean;
  expanded: boolean;
  pathname: string;
  onToggle: (event: React.MouseEvent) => void;
  /** Following the section's own link, which also drops any manual collapse so the section opens. */
  onOpen: () => void;
  onNavigate?: () => void;
}) {
  const activeSubTo = findActiveSubItemTo(pages, pathname);
  // Books pages share one period in the URL (D49); moving between them keeps it.
  const withPeriod = usePeriodLink();
  const href = (target: string) => (target.startsWith("/books") ? withPeriod(target) : target);

  return (
    <div className="flex flex-col">
      <Link
        to={href(to)}
        onClick={onOpen}
        className={cn(
          "group flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition",
          active ? "font-semibold text-foreground" : "text-foreground hover:bg-muted",
        )}
      >
        <Icon className={cn("size-4 shrink-0", active ? "text-accent-blue" : "text-muted-foreground")} />
        <span className="flex-1">{label}</span>
        <button
          type="button"
          onClick={onToggle}
          aria-label={expanded ? "Collapse section" : "Expand section"}
          aria-expanded={expanded}
          className="grid size-5 shrink-0 place-items-center rounded text-muted-foreground transition hover:bg-muted-foreground/10 hover:text-foreground"
        >
          <ChevronDown
            className={cn("size-3.5 transition-transform", expanded ? "rotate-0" : "-rotate-90")}
          />
        </button>
      </Link>

      {expanded && (
        <div className="mt-0.5 flex flex-col gap-0.5 pb-1 pl-5">
          {pages
            .filter((sub) => !sub.parent)
            .map((sub) => {
              const children = pages.filter((c) => c.parent === sub.to);
              const groupOpen = sub.to === activeSubTo || children.some((c) => c.to === activeSubTo);
              return (
                <Fragment key={sub.to}>
                  <SubNavLink page={sub} active={sub.to === activeSubTo} href={href} onNavigate={onNavigate} />
                  {children.length > 0 && groupOpen ? (
                    <div className="ml-[15px] flex flex-col gap-0.5 border-l pl-2">
                      {children.map((c) => (
                        <SubNavLink key={c.to} page={c} active={c.to === activeSubTo} href={href} onNavigate={onNavigate} child />
                      ))}
                    </div>
                  ) : null}
                </Fragment>
              );
            })}
        </div>
      )}
    </div>
  );
}

function SubNavLink({
  page,
  active,
  href,
  onNavigate,
  child = false,
}: {
  page: NavPage;
  active: boolean;
  href: (to: string) => string;
  onNavigate?: () => void;
  /** Nested under a group: no icon, like Mercury's report list. */
  child?: boolean;
}) {
  const { hasModule } = useModules();
  const SubIcon = page.icon;
  if (page.module && !hasModule(page.module)) {
    return <ForbiddenNavItem label={page.label} icon={SubIcon} module={page.module} nested />;
  }
  return (
    <Link
      to={href(page.to)}
      onClick={() => onNavigate?.()}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] transition",
        active ? "bg-accent-blue/10 font-semibold text-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      {child ? null : <SubIcon className={cn("size-3.5 shrink-0", active ? "text-accent-blue" : "text-muted-foreground")} />}
      <span>{page.label}</span>
    </Link>
  );
}

function ForbiddenNavItem({
  label,
  icon: Icon,
  module,
  nested = false,
}: {
  label: string;
  icon: LucideIcon;
  module: ModuleKey | readonly ModuleKey[];
  nested?: boolean;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* A div, not a Link: every request behind the route would 403. */}
        <div
          className={cn(
            "flex cursor-not-allowed items-center justify-between gap-2 rounded-lg text-muted-foreground opacity-55",
            nested ? "px-2.5 py-1.5 text-[13px]" : "px-3 py-2 text-sm",
          )}
          aria-disabled
        >
          <span className="flex items-center gap-2.5">
            <Icon className={cn("shrink-0 text-muted-foreground", nested ? "size-3.5" : "size-4")} />
            {label}
          </span>
          <Lock className="size-3 shrink-0" />
        </div>
      </TooltipTrigger>
      <TooltipContent side="right">{moduleForbiddenMessage(module)}</TooltipContent>
    </Tooltip>
  );
}

function AgentStatusDot({
  status,
}: {
  status: "online" | "thinking" | "offline";
}) {
  const color =
    status === "offline"
      ? "bg-red-500"
      : status === "thinking"
        ? "bg-amber-500"
        : "bg-emerald-500";
  return (
    <span className="relative inline-flex size-2 shrink-0">
      <span
        className={cn(
          "absolute inset-0 rounded-full opacity-60",
          color,
          status !== "offline" && "animate-pulse",
        )}
      />
      <span className={cn("relative size-2 rounded-full", color)} />
    </span>
  );
}
